import type { Metadata } from "next"
import * as cheerio from "cheerio"
import type { DirectusBlogPost } from "@/lib/directus"
import type { Author } from "@/lib/types/author"
import {
  calculateReadingTime,
  extractFAQFromContent,
  getManualFAQs,
  getManualHowTo,
  normalizeFAQItems,
  shouldIncludeFAQSchema,
  type FAQItem,
} from "@/lib/content-analyzers"
import { getCanonicalImageUrl } from "@/lib/directus"
import {
  buildAttributedImageObject,
  CC_LICENSE_URLS,
  generateHowToSchema,
} from "@/lib/structured-data"
import { fitMetadataDescription, fitMetadataTitle } from "@/lib/metadata-text"
import { filterInternalBlogTags } from "@/lib/internal-blog-tags"
import { getDatasetSchemaId } from "@/data/blog-dataset-schema"

// The license-label → license-URI map now lives in lib/structured-data.ts so
// compare/weeds/pest pages can build the same license-bearing ImageObjects
// without importing the blog HTML parser. Re-exported here for the existing
// importers (app/guides/[slug]/page.tsx, tests).
export { CC_LICENSE_URLS }
// Alt-text resolution is a leaf module (lib/blog-image-alt) so the client-side
// blog cards can use it without importing cheerio or the Directus SDK.
export { resolveBlogImageAlt } from "@/lib/blog-image-alt"

/**
 * Per-image metadata authored in Directus (`blog_posts.content_image_meta`),
 * keyed by the raw `src` of the in-body <img>. Feeds ImageObject nodes for
 * body photos that carry no figcaption to parse.
 */
export interface ContentImageMetaEntry {
  alt?: string | null
  caption?: string | null
  credit?: string | null
  license?: string | null
  sourceUrl?: string | null
}

export type ContentImageMetaMap = Record<string, ContentImageMetaEntry>

/**
 * `content_image_meta` arrives as a JSON column: already an object on most
 * reads, a string on some Directus responses, and null for the vast majority of
 * posts. Anything that isn't a plain src→object map is treated as absent.
 */
export function normalizeContentImageMeta(
  value: unknown,
): ContentImageMetaMap {
  if (!value) return {}
  let parsed: unknown = value
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value)
    } catch {
      return {}
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
  const out: ContentImageMetaMap = {}
  for (const [src, entry] of Object.entries(parsed as Record<string, unknown>)) {
    if (entry && typeof entry === "object" && !Array.isArray(entry)) {
      out[src] = entry as ContentImageMetaEntry
    }
  }
  return out
}

// Static brand card rendered by /og-image-v4.jpg. The one image on the site
// with a guaranteed 1200x630 16:9 crop, so it is the tail entry of every
// Article.image array and the sole entry when a post has no photo of its own.
const BRAND_OG_CARD_URL = "https://whatgrassisthis.com/og-image-v4.jpg"
const BRAND_OG_CARD_IMAGE = {
  "@type": "ImageObject" as const,
  url: BRAND_OG_CARD_URL,
  width: 1200,
  height: 630,
}

interface ExtractedImageObject {
  "@type": "ImageObject"
  contentUrl: string
  name?: string
  description?: string
  caption?: string
  creditText?: string
  license?: string
  acquireLicensePage?: string
}

/**
 * Scans blog post HTML for attributed <figure> blocks (img + figcaption +
 * optional outer <a> to a source page) and returns ImageObject JSON-LD nodes
 * for each one. Used to expose grass-identification reference photos and other
 * attributed images to Google Image Search via BlogPosting.image[].
 *
 * Returns [] when no qualifying figures exist (the common case for most posts).
 * Qualifying figures must have:
 *   - an <img> with a src
 *   - a <figcaption> with attribution text
 * The outer link, when present, is used as acquireLicensePage. The figcaption
 * is parsed for the species name (bolded prefix), description (rest of the
 * caption), photographer (after "Photo:" or "Illustration:"), and a CC license
 * tag matched against CC_LICENSE_URLS.
 */
export function extractImageObjectsFromContent(
  content: string,
  contentImageMeta?: unknown,
): ExtractedImageObject[] {
  const meta = normalizeContentImageMeta(contentImageMeta)
  const hasMeta = Object.keys(meta).length > 0
  if (!content) return []
  if (!content.includes("<figure") && !hasMeta) return []

  const $ = cheerio.load(content)
  const objects: ExtractedImageObject[] = []
  // Every <img> already accounted for by a figcaption-parsed figure, keyed by
  // raw src, so the content_image_meta pass below cannot emit a duplicate node
  // for the same picture.
  const claimedSrcs = new Set<string>()

  $("figure").each((_index, el) => {
    const $fig = $(el)
    const img = $fig.find("img").first()
    const src = img.attr("src")
    if (!src) return

    const altText = img.attr("alt")?.trim()
    const captionEl = $fig.find("figcaption").first()
    if (!captionEl.length) return

    const captionText = captionEl.text().replace(/\s+/g, " ").trim()
    if (!captionText) return

    // The outer anchor (when present) points to the source page on Wikimedia
    // Commons or another origin. Used as acquireLicensePage.
    const sourceLink = $fig.find("a[href]").first().attr("href")

    // Name: the bolded prefix of the figcaption when present, otherwise the
    // alt text up to the first comma.
    const boldName = captionEl.find("strong").first().text().trim()
    const name = boldName || altText?.split(",")[0]?.trim()

    // Credit: text after "Photo:" or "Illustration:" up to a period or slash.
    const creditMatch = captionText.match(
      /(?:Photo|Illustration|Image):\s*([^./]+?)(?:\s*\/|\.|$)/i,
    )
    const creditText = creditMatch?.[1]?.trim()

    // License: scan for any of the known CC tags in the figcaption.
    let license: string | undefined
    for (const [label, url] of Object.entries(CC_LICENSE_URLS)) {
      if (captionText.includes(label)) {
        license = url
        break
      }
    }

    const obj: ExtractedImageObject = {
      "@type": "ImageObject",
      contentUrl: canonicalContentImageUrl(src, altText),
    }
    if (name) obj.name = name
    if (altText && altText !== name) obj.description = altText
    if (captionText) obj.caption = captionText
    if (creditText) obj.creditText = creditText
    if (license) obj.license = license
    if (sourceLink) obj.acquireLicensePage = sourceLink

    claimedSrcs.add(src)
    objects.push(obj)
  })

  // Second pass: bare <img> tags (no figure/figcaption to parse) whose src has
  // an authored entry in blog_posts.content_image_meta. Figcaption parsing
  // stays the primary path — an image the editor captioned on the page keeps
  // the caption the reader sees.
  if (hasMeta) {
    $("img").each((_index, el) => {
      const src = $(el).attr("src")
      if (!src || claimedSrcs.has(src)) return
      const entry = meta[src]
      if (!entry) return
      claimedSrcs.add(src)

      const altText = entry.alt?.trim() || $(el).attr("alt")?.trim() || undefined
      const node = buildAttributedImageObject(
        {
          src: canonicalContentImageUrl(src, altText),
          alt: altText,
          caption: entry.caption ?? undefined,
          credit: entry.credit ?? undefined,
          license: entry.license ?? undefined,
          sourceUrl: entry.sourceUrl ?? undefined,
        },
        { name: altText?.split(",")[0]?.trim() },
      )
      if (node) objects.push(node as ExtractedImageObject)
    })
  }

  return objects
}

/**
 * The URL an image crawler must see for a body photo: the same canonical
 * same-origin `/img/<id>/<slug>.jpg` string lib/content-processor renders into
 * the page's `src` and sitemap-image.xml advertises, made absolute. Directus
 * `/assets/` URLs are rewritten; local and third-party srcs pass through (with
 * root-relative paths gaining the site origin).
 */
function canonicalContentImageUrl(src: string, altText?: string): string {
  const canonical = src.includes("/assets/")
    ? getCanonicalImageUrl(src, altText || "lawn", true)
    : src
  return canonical.startsWith("/")
    ? `https://whatgrassisthis.com${canonical}`
    : canonical
}

interface ExtractedHowToStep {
  "@type": "HowToStep"
  name: string
  text: string
  url?: string
}

interface ExtractedHowTo {
  "@type": "HowTo"
  "@id"?: string
  name: string
  description?: string
  step: ExtractedHowToStep[]
}

/**
 * Scans blog content for a "How to ..." H2 followed by a series of
 * "Step N: ..." H3 subheadings and returns a HowTo JSON-LD object suitable
 * for Google's procedural-query rich results.
 *
 * Returns null when no qualifying pattern exists (the common case for most
 * posts). To qualify, the content must contain:
 *   - exactly one H2 whose text starts with "How to "
 *   - at least 3 H3s after that H2 (before the next H2) whose text starts
 *     with "Step N:" (where N is a digit). Looser matches don't trigger.
 *
 * postSlug is used to mint a stable `@id` for the HowTo node so it can be
 * referenced from other schema entities if needed.
 */
export function extractHowToFromContent(
  content: string,
  postSlug: string,
): ExtractedHowTo | null {
  if (!content) return null
  if (!/<h2[^>]*>\s*How to/i.test(content)) return null

  const $ = cheerio.load(content)
  let howToH2: ReturnType<typeof $> | null = null
  $("h2").each((_index, el) => {
    if (howToH2) return
    const text = $(el).text().trim()
    if (/^How to\b/i.test(text)) howToH2 = $(el)
  })
  if (!howToH2) return null

  // Walk the siblings after the "How to" H2 until the next H2. Collect step
  // H3s and the paragraphs that immediately follow each.
  const steps: ExtractedHowToStep[] = []
  let currentStep: ExtractedHowToStep | null = null
  let introParagraph: string | undefined
  let sawFirstStep = false

  // `cheerio.Cheerio` is typed loosely above; cast to any for nextAll().
  const siblings = (howToH2 as any).nextUntil("h2")
  siblings.each((_index: number, el: any) => {
    const $el = $(el)
    const tag = (el as { tagName?: string }).tagName?.toLowerCase()

    if (tag === "h3") {
      const headingText = $el.text().trim()
      const stepMatch = headingText.match(/^Step\s+(\d+)\s*[:\-—]\s*(.+)$/i)
      if (stepMatch) {
        if (currentStep) steps.push(currentStep)
        currentStep = {
          "@type": "HowToStep",
          name: stepMatch[2].trim(),
          text: "",
        }
        sawFirstStep = true
      } else {
        // Non-step H3 inside the How-to section terminates step capture; flush
        // the current step (if any) and stop collecting further steps.
        if (currentStep) {
          steps.push(currentStep)
          currentStep = null
        }
      }
      return
    }

    if (tag === "p") {
      const text = $el.text().replace(/\s+/g, " ").trim()
      if (!text) return
      if (currentStep) {
        // Append paragraph text to the current step's body, capped at a
        // reasonable length to avoid bloating the schema payload.
        if (currentStep.text) {
          currentStep.text = `${currentStep.text} ${text}`.slice(0, 1200)
        } else {
          currentStep.text = text.slice(0, 1200)
        }
      } else if (!sawFirstStep && !introParagraph) {
        introParagraph = text.slice(0, 500)
      }
      return
    }

    // ul/ol immediately under a step add bullet text to the step body too.
    if ((tag === "ul" || tag === "ol") && currentStep) {
      const bullets = $el
        .find("li")
        .map((__i: number, li: any) => $(li).text().replace(/\s+/g, " ").trim())
        .get()
        .filter(Boolean)
        .join(" ")
      if (bullets) {
        currentStep.text = currentStep.text
          ? `${currentStep.text} ${bullets}`.slice(0, 1200)
          : bullets.slice(0, 1200)
      }
    }
  })

  if (currentStep) steps.push(currentStep)

  // Require at least 3 real steps with non-empty text to qualify. Below that,
  // Google's HowTo rich result eligibility falls off and the schema reads as
  // padding.
  const usableSteps = steps.filter((s) => s.text && s.text.length > 30)
  if (usableSteps.length < 3) return null

  const howTo: ExtractedHowTo = {
    "@type": "HowTo",
    "@id": `https://whatgrassisthis.com/blog/${postSlug}#howto`,
    name: ((howToH2 as any).text() as string).trim(),
    step: usableSteps,
  }
  if (introParagraph) howTo.description = introParagraph

  return howTo
}

export type BlogPostPageData = Omit<
  DirectusBlogPost,
  "image_url" | "date_modified" | "faqs"
> & {
  image_url?: string | null
  date_modified?: string | null
  faqs?: FAQItem[] | string | null
  image_alt?: string | null
  content_image_meta?: ContentImageMetaMap | string | null
}

/**
 * Did this post get a real update, or was it just touched after publishing?
 *
 * Must be judged at DAY granularity, because that is the granularity the reader
 * sees: formatBlogDate renders both dates as "July 22, 2026" (timeZone UTC).
 * Comparing raw ISO timestamps — the previous behaviour — meant a post edited
 * minutes after publishing rendered "July 22, 2026 · Updated July 22, 2026",
 * which reads as a bug and burns a line of the meta card saying nothing.
 *
 * Conservative on bad input: if either date is missing or unparseable we cannot
 * prove an update happened, so we don't claim one.
 */
export function isMeaningfulUpdate(
  datePublished?: string | null,
  dateModified?: string | null
): boolean {
  if (!datePublished || !dateModified) return false
  const published = new Date(datePublished)
  const modified = new Date(dateModified)
  if (Number.isNaN(published.getTime()) || Number.isNaN(modified.getTime())) {
    return false
  }
  return (
    published.getUTCFullYear() !== modified.getUTCFullYear() ||
    published.getUTCMonth() !== modified.getUTCMonth() ||
    published.getUTCDate() !== modified.getUTCDate()
  )
}

export function getBlogDateInfo(blogPost: BlogPostPageData) {
  const datePublished = blogPost.date_published ?? blogPost.date_created
  const dateModified = blogPost.date_modified ?? undefined
  const hasActualUpdate = isMeaningfulUpdate(datePublished, dateModified)
  const effectiveDateModified = hasActualUpdate ? dateModified : datePublished

  return {
    datePublished,
    dateModified,
    hasActualUpdate,
    effectiveDateModified,
  }
}

export function formatBlogDate(date: string): string {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  })
}

const HOWTO_TAG_MARKERS = new Set([
  "how-to",
  "how to",
  "guide",
  "step-by-step",
  "tutorial",
  "schedule",
])

function detectHowToPost(blogPost: BlogPostPageData): boolean {
  const tags = Array.isArray(blogPost.tags) ? blogPost.tags : []
  for (const tag of tags) {
    if (HOWTO_TAG_MARKERS.has(String(tag).toLowerCase())) return true
  }

  const slug = (blogPost.slug || "").toLowerCase()
  if (slug.startsWith("how-to-") || slug.startsWith("when-to-")) return true

  const title = blogPost.title || ""
  if (/^(how (to|do|much|long)|when (to|should))\b/i.test(title)) return true

  return false
}

export function buildBlogMetadata(
  blogPost: BlogPostPageData,
  slug: string
): Metadata {
  const { datePublished, effectiveDateModified } = getBlogDateInfo(blogPost)
  const description = fitMetadataDescription(
    blogPost.meta_description ||
    blogPost.excerpt ||
    `Learn about ${blogPost.category.toLowerCase()} and get expert lawn care tips from What Grass Is This?.`
  )
  // og:image is the same canonical URL the hero renders, BlogPosting.image
  // names and sitemap-image.xml advertises. Social crawlers cache it forever,
  // and an image crawler that follows it lands on an indexable, same-origin
  // photo rather than the Disallow-all Directus host.
  const ogImageUrl = blogPost.image_url
    ? blogPost.image_url
    : blogPost.image
      ? getCanonicalImageUrl(blogPost.image, slug, true)
      : null
  const seoTitle = fitMetadataTitle(
    blogPost.meta_title || `${blogPost.title} | What Grass Is This?`
  )

  // article:author should resolve to a profile URL per the OG spec. If
  // blogPost.author is a kebab-case slug (the canonical case for our three
  // writer personas), build the profile URL; otherwise fall back to a name
  // string so we don't fabricate a 404-prone profile link.
  const authorSlug = blogPost.author && /^[a-z0-9-]+$/.test(blogPost.author)
    ? blogPost.author
    : null
  const authorProfileUrl = authorSlug
    ? `https://whatgrassisthis.com/blog/author/${authorSlug}`
    : null

  const metadata: Metadata = {
    title: seoTitle,
    description,
    keywords:
      filterInternalBlogTags(blogPost.tags).join(", ") ||
      `${blogPost.category}, lawn care, grass types, gardening`,
    authors: [{ name: blogPost.author || "Lawn Care Expert" }],
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        "max-video-preview": -1,
        "max-image-preview": "large",
        "max-snippet": -1,
      },
    },
    openGraph: {
      title: blogPost.title,
      description,
      type: "article",
      url: `https://whatgrassisthis.com/blog/${slug}`,
      siteName: "What Grass Is This?",
      locale: "en_US",
      publishedTime: datePublished,
      modifiedTime: effectiveDateModified,
      authors: [authorProfileUrl || blogPost.author || "Lawn Care Expert"],
      section: blogPost.category,
      tags: blogPost.tags || [blogPost.category],
    },
    twitter: {
      card: "summary_large_image",
      title: blogPost.title,
      description,
    },
    alternates: {
      canonical: `https://whatgrassisthis.com/blog/${slug}`,
    },
  }

  if (ogImageUrl) {
    // Width only. The canonical image is resized to 1200px on its longest edge
    // with fit=inside, so its height follows the source aspect ratio and a
    // declared 630 would be a lie for most posts.
    metadata.openGraph!.images = [
      {
        url: ogImageUrl,
        width: 1200,
        alt: blogPost.title,
      },
    ]
    metadata.twitter!.images = [ogImageUrl]
  } else {
    metadata.openGraph!.images = [
      {
        url: "https://whatgrassisthis.com/og-image-v4.jpg",
        width: 1200,
        height: 630,
        alt: "What Grass Is This?",
      },
    ]
  }

  return metadata
}

export function buildBlogStructuredData(
  blogPost: BlogPostPageData,
  author: Author,
  // The resolved schema FAQ list from resolveBlogFaqs. When provided (even
  // empty) it is used verbatim — the schema must match what the page renders,
  // so no recompute or fallback may widen it. Omitted only by the admin
  // preview, which keeps the legacy derivation.
  resolvedFaqs?: FAQItem[],
  // Whether the FAQ accordion component renders on this page. The speakable
  // [data-faq-answer] selector only exists in the accordion's markup, so a
  // prose-FAQ page (e.g. the types-of-grass-in-* set) must not advertise it.
  hasFaqAccordion: boolean = true
) {
  const { datePublished, effectiveDateModified } = getBlogDateInfo(blogPost)
  const hasRealCoverPhoto = Boolean(blogPost.image_url || blogPost.image)
  const coverImageUrl =
    blogPost.image_url ||
    (blogPost.image
      ? getCanonicalImageUrl(blogPost.image, blogPost.slug, true)
      : BRAND_OG_CARD_URL)

  // Cover image: the primary BlogPosting.image entry. Falls back to the site
  // OG card so Google's rich-results image requirement is always satisfied.
  const coverImage = {
    "@type": "ImageObject" as const,
    url: coverImageUrl,
    // No height: fit=inside preserves the source aspect ratio, so only the
    // width is known ahead of the fetch.
    width: 1200,
  }

  // Append attributed figures from the post body (e.g. species reference
  // photos on the grass-identification post) so Google Image Search can index
  // them with proper license + credit data and surface the page in image-pack
  // SERPs. Most posts have no qualifying figures.
  const bodyImageObjects = extractImageObjectsFromContent(
    blogPost.content || "",
    blogPost.content_image_meta,
  )
  // Google reads Article.image as a ranked list: real photos of the subject
  // first, the generated brand card last, so an image result for this page is
  // a lawn and not a logo. The card still ships as a guaranteed 1200x630 16:9
  // asset for the rich-result aspect-ratio requirement. Posts with no cover
  // photo of their own emit the card alone, as before.
  const imageNode = hasRealCoverPhoto
    ? [coverImage, ...bodyImageObjects, BRAND_OG_CARD_IMAGE]
    : bodyImageObjects.length > 0
      ? [...bodyImageObjects, coverImage]
      : coverImage

  // Original-data studies emit a Dataset node (data/blog-dataset-schema.ts,
  // rendered from app/blog/[slug]/page.tsx). Point the BlogPosting at it so
  // the study and its data resolve as one connected entity.
  const datasetId = getDatasetSchemaId(blogPost.slug)

  const baseStructuredData: any = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `https://whatgrassisthis.com/blog/${blogPost.slug}#blogpost`,
        headline: blogPost.title,
        description:
          blogPost.excerpt ||
          `Learn about ${blogPost.category.toLowerCase()} and get expert lawn care tips.`,
        image: imageNode,
        author: {
          "@type": "Person",
          "@id": `https://whatgrassisthis.com/blog/author/${author.slug}#author`,
          name: author.name,
          url: `https://whatgrassisthis.com/blog/author/${author.slug}`,
          image: `https://whatgrassisthis.com${author.headshot.url}`,
          description: author.bio.short,
          jobTitle: author.shortCredential.split(" | ")[0],
          knowsAbout: author.specializations,
        },
        // Reference the canonical Organization node from app/layout.tsx by
        // @id so we don't duplicate the full publisher object on every blog
        // post. Google's parser resolves the reference across the page graph.
        publisher: { "@id": "https://whatgrassisthis.com#organization" },
        datePublished,
        dateModified: effectiveDateModified,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://whatgrassisthis.com/blog/${blogPost.slug}`,
        },
        articleSection: blogPost.category,
        keywords:
          filterInternalBlogTags(blogPost.tags).join(", ") ||
          `${blogPost.category}, lawn care, grass types, gardening`,
        wordCount: blogPost.content?.replace(/<[^>]*>/g, "").split(" ").length || 0,
        timeRequired: `PT${
          blogPost.reading_time || calculateReadingTime(blogPost.content || "")
        }M`,
        inLanguage: "en-US",
        isAccessibleForFree: true,
        about: [
          {
            "@type": "Thing",
            name: "Lawn Care",
          },
          {
            "@type": "Thing",
            name: blogPost.category,
          },
        ],
        ...(datasetId ? { isBasedOn: { "@id": datasetId } } : {}),
        potentialAction: {
          "@type": "ReadAction",
          target: `https://whatgrassisthis.com/blog/${blogPost.slug}`,
        },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: hasFaqAccordion
            ? [".article-lede", "[data-faq-answer]"]
            : [".article-lede"],
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `https://whatgrassisthis.com/blog/${blogPost.slug}#breadcrumb`,
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://whatgrassisthis.com" },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://whatgrassisthis.com/blog" },
          { "@type": "ListItem", "position": 3, "name": blogPost.category, "item": `https://whatgrassisthis.com/blog/category/${blogPost.category.toLowerCase().replace(/\s+/g, '-').replace(/[&]/g, 'and')}` },
          { "@type": "ListItem", "position": 4, "name": blogPost.title }
        ]
      },
    ],
  }

  const manualFAQs = getManualFAQs(blogPost.slug)
  const directusFAQs = normalizeFAQItems(blogPost.faqs)
  const faqs =
    resolvedFaqs !== undefined
      ? resolvedFaqs
      : manualFAQs.length > 0
        ? normalizeFAQItems(manualFAQs)
        : directusFAQs.length > 0
          ? directusFAQs
          : shouldIncludeFAQSchema(blogPost.content || "", blogPost.category)
            ? extractFAQFromContent(blogPost.content || "")
            : []

  if (faqs.length > 0) {
    baseStructuredData["@graph"].push({
      "@type": "FAQPage",
      "@id": `https://whatgrassisthis.com/blog/${blogPost.slug}#faq`,
      mainEntity: faqs.map((faq: FAQItem) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    })
  }

  // HowTo schema. Two sources, in priority order:
  //   1. A curated MANUAL_POST_HOWTOS block (for high-traffic posts whose steps
  //      are <ol> lists, which the content extractor can't recognize). Steps
  //      mirror the on-page copy, so the schema still matches the page.
  //   2. Otherwise extractHowToFromContent, which emits only when the content
  //      has a "How to ..." H2 followed by 3+ "Step N: ..." H3s with body text.
  //      Returns null for most posts, so they carry no HowTo node.
  // Google deprecated the desktop HowTo rich result in 2023, but AI Overviews /
  // Perplexity / Bing still parse and cite it.
  const pageUrl = `https://whatgrassisthis.com/blog/${blogPost.slug}`
  const manualHowTo = getManualHowTo(blogPost.slug)
  if (manualHowTo) {
    baseStructuredData["@graph"].push({
      ...generateHowToSchema(manualHowTo, pageUrl),
      "@id": `${pageUrl}#howto`,
    })
  } else {
    const howTo = extractHowToFromContent(blogPost.content || "", blogPost.slug)
    if (howTo) {
      baseStructuredData["@graph"].push(howTo)
    }
  }

  return baseStructuredData
}
